import { useEffect, useState } from 'react'
import { Alert, Input, Select, Space } from 'antd'
import { BankOutlined, FileTextOutlined } from '@ant-design/icons'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton } from '@/widgets/demo/ListPrimitives'
import { useAppNavigate } from '../../hooks/useAppNavigate'
import { isHandledAuthError, municipalitiesApi } from '@/services'
import { noticeSettingsApi, type NoticeSettingItem } from '@/services/noticeSettings'
import { usePermission } from '@/hooks/usePermission'
import { useToast } from '@/widgets/common'

type MunicipalityOption = { id: number; name: string; code: string }

/**
 * 決定通知書設定（自治体ごとの設定項目）の管理画面。
 *
 * 第1項目は決定通知書の敬称(殿/様)。自治体ごとに汎用 key-value で保持し、将来項目はカタログ追加のみで拡張可能。
 * ADMIN は自分の自治体、SYSTEM_ADMIN は上部の「対象自治体」で選んだ自治体を維護する（ユーザ管理と同方式）。
 * ユーザ管理と同じ USER_MGMT 権限で保護。
 */
export default function NoticeSettingsMgmt() {
  const navigate = useAppNavigate()
  const toast = useToast()
  const { isSystemAdmin } = usePermission()
  const [municipalities, setMunicipalities] = useState<MunicipalityOption[]>([])
  const [targetMunicipalityId, setTargetMunicipalityId] = useState<number | null>(null)
  const [items, setItems] = useState<NoticeSettingItem[]>([])
  const [loading, setLoading] = useState(false)
  const [savingKey, setSavingKey] = useState<string | null>(null)

  useEffect(() => {
    if (!isSystemAdmin) return
    municipalitiesApi
      .list()
      .then(({ data }) => setMunicipalities(data))
      .catch((error) => {
        if (!isHandledAuthError(error)) toast.error('自治体一覧の取得に失敗しました')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystemAdmin])

  const needsSelection = isSystemAdmin && targetMunicipalityId == null

  const load = () => {
    if (needsSelection) {
      setItems([])
      return
    }
    setLoading(true)
    const req = isSystemAdmin
      ? noticeSettingsApi.listForManagement(targetMunicipalityId as number)
      : noticeSettingsApi.listForMyMunicipality()
    req
      .then(({ data }) => setItems(data ?? []))
      .catch((error) => {
        if (!isHandledAuthError(error)) toast.error('決定通知書設定の取得に失敗しました')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystemAdmin, targetMunicipalityId])

  const saveItem = async (key: string, value: string) => {
    if (savingKey != null) return
    setSavingKey(key)
    try {
      const { data } = isSystemAdmin
        ? await noticeSettingsApi.saveForManagement(targetMunicipalityId as number, { key, value })
        : await noticeSettingsApi.saveForMyMunicipality({ key, value })
      setItems((prev) => prev.map((it) => (it.key === key ? data : it)))
      toast.success('保存しました')
    } catch (error) {
      if (!isHandledAuthError(error)) toast.error('保存に失敗しました')
      load() // 失敗時は表示を実値へ戻す
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader
        breadcrumbs={[
          { label: 'システム管理', page: 'submenu-admin' },
          { label: '決定通知書設定' },
        ]}
      />
      <div className="mx-auto max-w-5xl px-6 py-8">
        <BackButton onClick={() => navigate('submenu-admin')} />

        {/* ヘッダーカード */}
        <div className="mt-2 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="flex items-start gap-4 p-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-md shadow-rose-200">
              <FileTextOutlined style={{ fontSize: 22 }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">決定通知書設定</h1>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-500">
                決定通知書まわりの自治体別設定です。現在は「決定通知書の敬称（殿／様）」を設定できます。各項目は変更すると即時に保存されます。
              </p>
            </div>
          </div>

          {isSystemAdmin ? (
            <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-3">
              <BankOutlined className="text-gray-400" />
              <span className="text-sm font-medium text-gray-600">対象自治体</span>
              <Select<number>
                style={{ minWidth: 300 }}
                placeholder="設定する自治体を選択"
                value={targetMunicipalityId ?? undefined}
                onChange={(v) => setTargetMunicipalityId(v ?? null)}
                showSearch
                optionFilterProp="label"
                options={municipalities.map((m) => ({ value: m.id, label: `${m.name}（${m.code}）` }))}
              />
            </div>
          ) : null}
        </div>

        {/* コンテンツカード */}
        <div className="mt-4 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          {needsSelection ? (
            <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
                <BankOutlined style={{ fontSize: 28 }} />
              </div>
              <p className="mt-4 text-sm font-semibold text-gray-700">設定する自治体を選択してください</p>
              <p className="mt-1 max-w-md text-xs leading-relaxed text-gray-400">
                システム管理者は自治体に属さないため、上の「対象自治体」から選ぶと、その自治体の決定通知書設定を編集できます。
              </p>
            </div>
          ) : loading ? (
            <div className="px-6 py-16 text-center text-sm text-gray-400">読み込み中...</div>
          ) : items.length === 0 ? (
            <div className="px-6 py-16 text-center text-sm text-gray-400">設定項目がありません。</div>
          ) : (
            <div>
              {items.map((item) => (
                <div
                  key={item.key}
                  className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-6 py-4 last:border-b-0"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-700">{item.label}</div>
                    <div className="text-xs text-gray-400">キー: {item.key}</div>
                  </div>
                  <div>
                    {item.inputType === 'select' ? (
                      <Select
                        value={item.value}
                        style={{ minWidth: 160 }}
                        disabled={savingKey === item.key}
                        onChange={(v) => void saveItem(item.key, v)}
                        options={item.options.map((o) => ({ value: o, label: o }))}
                      />
                    ) : (
                      <Space>
                        <Input
                          defaultValue={item.value}
                          style={{ width: 240 }}
                          disabled={savingKey === item.key}
                          onPressEnter={(e) => void saveItem(item.key, (e.target as HTMLInputElement).value)}
                          onBlur={(e) => {
                            if (e.target.value !== item.value) void saveItem(item.key, e.target.value)
                          }}
                        />
                      </Space>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Alert
          className="mt-4"
          type="info"
          showIcon
          message="敬称は決定通知書PDFの宛名（受給者名のあとの「殿／様」）に反映されます。計算（決定通知書出力）時点の設定が使われます。"
        />
      </div>
    </div>
  )
}
